import { useContext, useMemo } from 'react'
import { SessionDataContext } from './context/SessionDataContext'
import { TelemetryDataContext } from './context/TelemetryDataContext'
import { ActivityContext } from './context/ActivityContext'
import TaskDescription from './components/TaskDescription'
import ClassOverview from './components/ClassOverview'
import ClassProgress from './components/ClassProgress'
import ClassCodingTimeline from './components/ClassCodingTimeline'
import TimelineLegend from './components/TimelineLegend'
import LatestConversation from './components/LatestConversation'
import '../../css/instructorDash/instructorDashboard.scss'

// `focusRequest` (optional, see InstructorDashboardPanel): open that student's detail.
export default function InstructorDashboard({ students, focusRequest = null, onFocusHandled }) {
  const { sessionData, loading: sessionLoading } = useContext(SessionDataContext)
  const { eventsByUser, loading: telemetryLoading } = useContext(TelemetryDataContext)
  const { loading: activityLoading } = useContext(ActivityContext)

  // Only show students who have telemetry events (already filtered by time window)
  const filteredStudents = useMemo(() => {
    const matched = students.filter(s => {
      const uid = String(s.id || s.user_id)
      const events = eventsByUser[uid]
      if (!events) return false
      if (!events.some(e => e.type !== 'SESSION_START')) return false
      // Exclude sessions under 15 seconds
      const first = events[0]?.timestamp
      const last = events[events.length - 1]?.timestamp
      if (!first || !last || (last - first) < 15000) return false
      return true
    })
    console.log(`Students filtered: ${students.length} → ${matched.length}`)
    return matched
  }, [students, eventsByUser])

  if (sessionLoading || telemetryLoading || activityLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '16px',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #e0e0e0',
          borderTopColor: '#0070CC',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <div style={{ fontSize: '15px', color: '#666', fontWeight: 500 }}>
          Loading dashboard data...
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  return (
    <div className="instructor-dashboard">
      <div className="dashboard-left">
        <TaskDescription />
        <ClassOverview students={filteredStudents} focusRequest={focusRequest} onFocusHandled={onFocusHandled} />
        <ClassProgress />
      </div>
      <div className="dashboard-middle">
        <ClassCodingTimeline />
        <TimelineLegend />
      </div>
      {/* Session Controls (a second, dead session-timer widget backed by unused
          SessionDataContext state, disconnected from the Live sub-tab's real timer) was
          removed from here - it's now part of the merged "Session Control" top-level tab,
          which has the one real timer. Two contradictory timers one tab apart defeated the
          point of merging Performance/Analytics into a single "Insights" tab. */}
      <div className="dashboard-right">
        <LatestConversation students={filteredStudents} />
      </div>
    </div>
  )
}